import './segmented-button.css';

import { ISegmentedButtonData } from './segmented-button.interface';
import { Util } from '../../../utils/util';
import { ArrowScroller } from '../ArrowScroller/arrow-scroller';
import { LocalizationService } from '../../../services/localization-service';

/**
 * Represents a segmented button component with multiple selectable options.
 */
export class SegmentedButton {
    private readonly _item: ISegmentedButtonData;
    private readonly _containerElement: HTMLElement;
    private readonly _arrowScroller: ArrowScroller;

    /**
     * Initializes a new instance of the SegmentedButton class.
     * @param buttonData - Data representing the segmented button structure.
     * @param localizationService - Localizes the scroll arrows' labels.
     */
    constructor(buttonData: ISegmentedButtonData, localizationService?: LocalizationService) {
        this._item = buttonData;
        this._containerElement = this._createButtonGroup();
        this._setEqualButtonWidths();

        this._arrowScroller = new ArrowScroller(this._containerElement, { localizationService });
    }

    /**
     * Creates the segmented button group element.
     * @returns The HTML element containing the button group.
     */
    private _createButtonGroup(): HTMLElement {
        const groupContainer = document.createElement("div");
        groupContainer.id = `div_${this._item.buttonId}`;
        groupContainer.classList.add("segmented-button");
        groupContainer.setAttribute("role", "listbox");
        groupContainer.setAttribute("aria-label", this._item.ariaGroupLabel);

        for (const buttonInfo of this._item.buttons) {
            const button = document.createElement("button");
            button.id = `btn_${this._item.buttonId}_${buttonInfo.id}`;
            button.tabIndex = 0;

            const textWrapper = document.createElement("span");
            textWrapper.innerHTML = buttonInfo.text;
            button.appendChild(textWrapper);

            const stateSpan = document.createElement("span");
            stateSpan.classList.add("sr-only");
            stateSpan.dataset.stateLabel = "true";
            stateSpan.textContent = buttonInfo.selected ? " selected" : "";
            button.appendChild(stateSpan);

            button.setAttribute("role", "option");
            button.setAttribute("aria-selected", buttonInfo.selected.toString());
            button.classList.add("segmented-button-option");

            if (buttonInfo.selected)
                button.classList.add("active");

            if (buttonInfo.ltag)
                button.setAttribute("ltag", buttonInfo.ltag);

            if (buttonInfo.ltagj)
                button.setAttribute("ltagj", Util.toLtagj(buttonInfo.ltagj));

            button.addEventListener("click", (event: Event) => {
                const result = buttonInfo.function(event);
                if (result)
                    this._setActiveButton(button.id);
            });

            button.addEventListener("keydown", (event: KeyboardEvent) => {
                if (event.key === "Enter" || event.key === " " || event.key === "Spacebar") {
                    event.preventDefault();
                    const result = buttonInfo.function(event);
                    if (result)
                        this._setActiveButton(button.id);
                } else if (event.key === "ArrowLeft" || event.key === "ArrowUp") {
                    event.preventDefault();
                    const prev = button.previousElementSibling as HTMLButtonElement | null;
                    if (prev) {
                        button.tabIndex = -1;
                        prev.tabIndex = 0;
                        prev.focus();
                    }
                } else if (event.key === "ArrowRight" || event.key === "ArrowDown") {
                    event.preventDefault();
                    const next = button.nextElementSibling as HTMLButtonElement | null;
                    if (next) {
                        button.tabIndex = -1;
                        next.tabIndex = 0;
                        next.focus();
                    }
                }
            });

            button.addEventListener("focus", () => button.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" }));

            groupContainer.appendChild(button);
        }
        return groupContainer;
    }

    /**
     * Gets the HTML container element for the segmented button.
     */
    public get element(): HTMLElement {
        return this._arrowScroller.element;
    }

    /**
     * Updates the active button state when a button is clicked.
     * @param buttonId - The ID of the clicked button.
     */
    private readonly _setActiveButton = (id: string): void => {
        const buttons = Array.from(this._containerElement.querySelectorAll("button"));
        for (const button of buttons) {
            button.classList.remove("active");
            button.setAttribute("aria-selected", "false");
            const stateSpan = button.querySelector("[data-state-label]");
            if (stateSpan)
                stateSpan.textContent = "";
        }

        const activeOption = document.getElementById(id) as HTMLButtonElement | null;
        if (activeOption) {
            this._activateButton(activeOption);
        } else {
            const defaultOption = buttons[0];
            if (defaultOption)
                this._activateButton(defaultOption);
        }
    }

    /**
    * Activates a button.
    * @param {HTMLButtonElement} button - The button to activate.
    */
    private _activateButton(button: HTMLButtonElement): void {
        button.classList.add("active");
        button.setAttribute("aria-selected", "true");
        const stateSpan = button.querySelector("[data-state-label]");
        if (stateSpan)
            stateSpan.textContent = " selected";
        button.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" });
    }

    /**
     * Ensures all buttons have the same width for a uniform appearance.
     */
    private _setEqualButtonWidths(): void {
        const tempContainer = document.createElement("div");
        Object.assign(tempContainer.style, {
            position: "absolute",
            visibility: "hidden",
            height: "auto",
            width: "auto"
        });
        document.body.appendChild(tempContainer);

        const tempElement = this._containerElement.cloneNode(true) as HTMLElement;
        tempContainer.appendChild(tempElement);

        // Updated selector from "button" to "[role='option']"
        const tempOptions = Array.from(tempElement.querySelectorAll("[role='option']")) as HTMLElement[];
        let maxWidth = 0;
        tempOptions.forEach(option => {
            maxWidth = Math.max(maxWidth, option.offsetWidth);
        });

        // Apply the maximum width to the original buttons
        const buttons = Array.from(this._containerElement.querySelectorAll("button"));
        buttons.forEach(button => {
            button.style.minWidth = `${maxWidth}px`;
        });

        document.body.removeChild(tempContainer);
    }
}